import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

export type ProviderKind =
  | 'openrouter'
  | 'openai'
  | 'lmstudio'
  | 'vllm'
  | 'ollama';
export type AIProvider = {
  id: number;
  name: string;
  kind: ProviderKind;
  model: string;
  base_url: string;
  has_api_key: boolean;
  is_active: boolean;
  enabled: boolean;
  bulk_only?: boolean;
  priority: number;
  context_window: number | null;
  max_output_tokens: number | null;
  timeout_seconds: number | null;
  supports_json_schema: boolean | null;
  supports_images: boolean | null;
  thinking_enabled: boolean;
  sampling_profile: string;
  extra: Record<string, unknown>;
};
export type ProviderInput = Omit<
  AIProvider,
  'id' | 'has_api_key' | 'is_active'
> & { api_key?: string };

export const providerError = (value: unknown): string => {
  if (value instanceof Error) return value.message;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(providerError).join(' ');
  if (value && typeof value === 'object')
    return Object.entries(value)
      .map(([key, nested]) => `${key}: ${providerError(nested)}`)
      .join(' ');
  return 'The provider request could not be completed.';
};

const request = async <T>(
  path: string,
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  data?: Partial<ProviderInput>,
) => {
  const response = await apiCaller({
    path: `/llm/providers${path}`,
    method,
    data,
    apiUrl: API_URL,
  });
  if (response.status < 200 || response.status >= 300)
    throw new Error(providerError(response.result));
  return response.result as T;
};

export const listProviders = () => request<AIProvider[]>('', 'GET');
export const createProvider = (data: ProviderInput) =>
  request<AIProvider>('', 'POST', data);
export const updateProvider = (id: number, data: Partial<ProviderInput>) =>
  request<AIProvider>(`/${id}`, 'PATCH', data);
export const deleteProvider = (id: number) => request<null>(`/${id}`, 'DELETE');
export const setPrimaryProvider = (id: number, active: boolean) =>
  request<AIProvider>(`/${id}/${active ? 'activate' : 'deactivate'}`, 'POST');
